'use client';
import React, { useState } from 'react';
import { Shield, FileText, CheckCircle, XCircle, AlertTriangle, Flag, Lock, ChevronRight, Download, RefreshCw, Clock, ArrowRight, Info, Send, ChevronLeft, Search, BarChart3, Filter, Inbox, AlertCircle, Bell, Zap, ThumbsUp, ThumbsDown, Plus, Minus, ExternalLink, Activity } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import MetricCard from '@/components/ui/MetricCard';

// ─── Types ────────────────────────────────────────────────────────────────────
type AppStatus = 'submitted' | 'under-review' | 'pending-info' | 'approved' | 'approved-conditions' | 'rejected';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type PEPTier = 'none' | 'tier1' | 'tier2' | 'tier3';
type SanctionStatus = 'clear' | 'partial-match' | 'confirmed-hit';
type ReviewDecision = 'approve' | 'approve-conditions' | 'reject' | null;
type InvestorStatusUpdate = 'eligible' | 'restricted' | 'blocked' | 'pending-edd' | null;

interface KYCApplication {
  id: string;
  refNo: string;
  name: string;
  type: 'Individual (KYC)' | 'Corporate (KYB)' | 'Institutional';
  jurisdiction: string;
  submittedAt: string;
  status: AppStatus;
  riskLevel: RiskLevel;
  assignee: string;
  pepTier: PEPTier;
  sanctionStatus: SanctionStatus;
  docsTotal: number;
  docsVerified: number;
  queue: string;
  email: string;
  incorporationNo?: string;
  fatcaStatus?: string;
  pepDeclared: boolean;
  uboCount?: number;
  sanctionScore?: number;
  amlFlags: number;
  lastActivity: string;
}

interface AuditEvent {
  ts: string;
  actor: string;
  action: string;
  detail: string;
  type: 'info' | 'warning' | 'critical' | 'success';
}

interface Condition {
  id: string;
  text: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const APPLICATIONS: KYCApplication[] = [
  {
    id: 'app-1', refNo: 'KYC-2026-00441', name: 'Thornfield Capital Holdings Ltd',
    type: 'Corporate (KYB)', jurisdiction: 'Cayman Islands', submittedAt: '2026-08-18 09:14',
    status: 'under-review', riskLevel: 'critical', assignee: 'L. Okafor',
    pepTier: 'tier2', sanctionStatus: 'partial-match', docsTotal: 8, docsVerified: 5,
    queue: 'EDD Queue', email: 'compliance@thornfieldcapital.ky',
    incorporationNo: 'CI-2019-88421', fatcaStatus: 'Passive NFFE', pepDeclared: true,
    uboCount: 3, sanctionScore: 87, amlFlags: 2, lastActivity: '2026-08-20 14:22',
  },
  {
    id: 'app-2', refNo: 'KYC-2026-00438', name: 'Priya Narasimhan',
    type: 'Individual (KYC)', jurisdiction: 'India', submittedAt: '2026-08-19 11:30',
    status: 'under-review', riskLevel: 'low', assignee: 'M. Patel',
    pepTier: 'none', sanctionStatus: 'clear', docsTotal: 5, docsVerified: 5,
    queue: 'Standard Queue', email: 'p.narasimhan@email.com',
    fatcaStatus: 'Non-US Person', pepDeclared: false,
    amlFlags: 0, lastActivity: '2026-08-20 10:05',
  },
  {
    id: 'app-3', refNo: 'KYC-2026-00435', name: 'Crestwood Family Office LLC',
    type: 'Corporate (KYB)', jurisdiction: 'Delaware, USA', submittedAt: '2026-08-17 15:00',
    status: 'pending-info', riskLevel: 'medium', assignee: 'L. Okafor',
    pepTier: 'none', sanctionStatus: 'clear', docsTotal: 11, docsVerified: 9,
    queue: 'Standard Queue', email: 'kyc@crestwoodfamily.com',
    incorporationNo: 'DE-2015-44821', fatcaStatus: 'US Person', pepDeclared: false,
    uboCount: 2, amlFlags: 0, lastActivity: '2026-08-19 16:44',
  },
  {
    id: 'app-4', refNo: 'KYC-2026-00429', name: 'Meridian Pension Trust APAC',
    type: 'Institutional', jurisdiction: 'Singapore', submittedAt: '2026-08-15 08:00',
    status: 'under-review', riskLevel: 'medium', assignee: 'M. Patel',
    pepTier: 'none', sanctionStatus: 'clear', docsTotal: 14, docsVerified: 11,
    queue: 'EDD Queue', email: 'compliance@meridianpension.sg',
    incorporationNo: 'SG-2010-00882', fatcaStatus: 'Non-US Person', pepDeclared: false,
    uboCount: 5, amlFlags: 1, lastActivity: '2026-08-20 09:30',
  },
  {
    id: 'app-5', refNo: 'KYC-2026-00421', name: 'Hargreaves Investment Co.',
    type: 'Corporate (KYB)', jurisdiction: 'United Kingdom', submittedAt: '2026-08-14 13:45',
    status: 'under-review', riskLevel: 'high', assignee: 'L. Okafor',
    pepTier: 'tier1', sanctionStatus: 'partial-match', docsTotal: 12, docsVerified: 8,
    queue: 'EDD Queue', email: 'kyc@hargreavesinv.co.uk',
    incorporationNo: 'UK-2008-77441', fatcaStatus: 'Non-US Person', pepDeclared: true,
    uboCount: 4, sanctionScore: 72, amlFlags: 3, lastActivity: '2026-08-20 11:07',
  },
  {
    id: 'app-6', refNo: 'KYC-2026-00418', name: 'Jan Kowalski',
    type: 'Individual (KYC)', jurisdiction: 'Poland', submittedAt: '2026-08-20 07:00',
    status: 'submitted', riskLevel: 'low', assignee: 'Unassigned',
    pepTier: 'none', sanctionStatus: 'clear', docsTotal: 5, docsVerified: 3,
    queue: 'Standard Queue', email: 'j.kowalski@email.pl',
    fatcaStatus: 'Non-US Person', pepDeclared: false,
    amlFlags: 0, lastActivity: '2026-08-20 07:00',
  },
];

const INITIAL_AUDIT: AuditEvent[] = [
  { ts: '2026-08-18 09:14', actor: 'System', action: 'Application Received', detail: 'KYC/KYB submission via self-service portal. Auto-assigned to EDD queue.', type: 'info' },
  { ts: '2026-08-18 10:02', actor: 'System', action: 'Sanctions Screening Initiated', detail: 'Automated OFAC SDN, EU Consolidated, UN Security Council screening started.', type: 'info' },
  { ts: '2026-08-19 11:30', actor: 'System', action: 'Sanctions Partial Match', detail: 'OFAC SDN partial match flagged for Dmitri Volkov (UBO-1). Score: 87/100. Manual review required.', type: 'critical' },
  { ts: '2026-08-19 14:00', actor: 'System', action: 'PEP Hit Detected', detail: 'Amelia Hartmann (UBO-2) matched as Tier-2 PEP — family member of German federal official.', type: 'warning' },
  { ts: '2026-08-20 08:45', actor: 'L. Okafor', action: 'Review Commenced', detail: 'Compliance officer commenced document verification for all 8 uploaded files.', type: 'info' },
  { ts: '2026-08-20 10:12', actor: 'L. Okafor', action: 'Document Rejected', detail: 'UBO Passport (Dmitri Volkov) rejected — possible OFAC SDN partial match. Escalated to MLRO.', type: 'warning' },
  { ts: '2026-08-20 14:22', actor: 'L. Okafor', action: 'AML Flag Raised', detail: 'Large transaction pattern detected. TXN-884921 ($2.85M) exceeds $1M threshold. STR consideration noted.', type: 'critical' },
];

const SANCTION_HITS = [
  { entity: 'Dmitri Volkov (UBO-1)', list: 'OFAC SDN', matchType: 'Partial Name Match', score: 87, status: 'pending-review' as const, detail: 'Possible match with Dmitri A. Volkov, Russian national, sanctioned 2022-03-15. DOB proximity: 3 years.' },
  { entity: 'Thornfield Capital Holdings Ltd', list: 'EU Consolidated', matchType: 'Partial Name Match', score: 61, status: 'pending-review' as const, detail: 'Partial match with Thornfield Holdings (BVI). Different jurisdiction — requires manual disambiguation.' },
];

const PEP_HITS = [
  { entity: 'Amelia Hartmann (UBO-2)', tier: 'Tier 2 PEP', relation: 'Family member of German Federal Minister', database: 'World-Check', matchDate: '2026-08-19', status: 'confirmed' as const },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function riskBadge(level: RiskLevel) {
  const map: Record<RiskLevel, { variant: 'active' | 'pending' | 'review' | 'rejected'; label: string }> = {
    low: { variant: 'active', label: 'Low Risk' },
    medium: { variant: 'pending', label: 'Medium Risk' },
    high: { variant: 'review', label: 'High Risk' },
    critical: { variant: 'rejected', label: 'Critical' },
  };
  return <StatusBadge variant={map[level].variant} label={map[level].label} dot />;
}

function appStatusBadge(status: AppStatus) {
  const map: Record<AppStatus, { variant: 'active' | 'pending' | 'review' | 'rejected'; label: string }> = {
    submitted: { variant: 'pending', label: 'Submitted' },
    'under-review': { variant: 'review', label: 'Under Review' },
    'pending-info': { variant: 'pending', label: 'Pending Info' },
    approved: { variant: 'active', label: 'Approved' },
    'approved-conditions': { variant: 'active', label: 'Approved w/ Conditions' },
    rejected: { variant: 'rejected', label: 'Rejected' },
  };
  return <StatusBadge variant={map[status].variant} label={map[status].label} dot />;
}

function sanctionBadge(status: SanctionStatus) {
  if (status === 'confirmed-hit') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-critical/15 text-critical border border-critical/30"><Shield size={10} /> Confirmed Hit</span>;
  if (status === 'partial-match') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-warning/15 text-warning border border-warning/30"><Shield size={10} /> Partial Match</span>;
  return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-positive/15 text-positive border border-positive/30"><Shield size={10} /> Clear</span>;
}

function pepBadge(tier: PEPTier) {
  if (tier === 'none') return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-surface-4 text-muted-foreground border border-border"><Flag size={10} /> No PEP</span>;
  const label = tier === 'tier1' ? 'PEP Tier 1' : tier === 'tier2' ? 'PEP Tier 2' : 'PEP Tier 3';
  return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-warning/15 text-warning border border-warning/30"><Flag size={10} /> {label}</span>;
}

// ─── Queue List ───────────────────────────────────────────────────────────────
function ApplicationQueueRow({ app, selected, onSelect }: { app: KYCApplication; selected: boolean; onSelect: () => void }) {
  const docsProgress = Math.round((app.docsVerified / app.docsTotal) * 100);
  return (
    <div
      onClick={onSelect}
      className={`p-3 rounded-xl border cursor-pointer transition-all ${selected ? 'border-primary/50 bg-primary/5' : 'border-border bg-surface-3 hover:border-border-hover hover:bg-surface-4'}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-semibold text-foreground truncate">{app.name}</p>
            {app.amlFlags > 0 && (
              <span className="flex-shrink-0 inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-critical/15 text-critical border border-critical/30">
                <AlertTriangle size={9} /> {app.amlFlags}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-mono-data">{app.refNo} · {app.type}</p>
        </div>
        <div className="flex-shrink-0">{riskBadge(app.riskLevel)}</div>
      </div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          {sanctionBadge(app.sanctionStatus)}
          {pepBadge(app.pepTier)}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-0.5">
            <p className="text-xs text-muted-foreground">Docs {app.docsVerified}/{app.docsTotal}</p>
            <p className="text-xs font-mono-data text-muted-foreground">{docsProgress}%</p>
          </div>
          <div className="h-1.5 rounded-full bg-surface-4 overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${docsProgress}%` }} />
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-muted-foreground">{app.assignee}</p>
          <p className="text-xs font-mono-data text-muted-foreground">{app.submittedAt.split(' ')[0]}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Sanction/PEP Flag Panel ──────────────────────────────────────────────────
function SanctionPEPPanel({ app, onAddAudit }: { app: KYCApplication; onAddAudit: (action: string, detail: string, type: AuditEvent['type']) => void }) {
  const [sanctionDispositions, setSanctionDispositions] = useState<Record<number, 'confirmed' | 'cleared' | null>>({});
  const [pepDispositions, setPepDispositions] = useState<Record<number, 'accepted' | 'escalated' | null>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const handleSanctionDispose = (idx: number, disposition: 'confirmed' | 'cleared') => {
    setSanctionDispositions((prev) => ({ ...prev, [idx]: disposition }));
    const hit = SANCTION_HITS[idx];
    onAddAudit(
      `Sanctions Hit ${disposition === 'confirmed' ? 'Confirmed' : 'Cleared'}`,
      `${hit.entity} — ${hit.list} (score: ${hit.score}). Disposition: ${disposition}.`,
      disposition === 'confirmed' ? 'critical' : 'success'
    );
  };

  const handlePEPDispose = (idx: number, disposition: 'accepted' | 'escalated') => {
    setPepDispositions((prev) => ({ ...prev, [idx]: disposition }));
    const hit = PEP_HITS[idx];
    onAddAudit(
      `PEP Hit ${disposition === 'accepted' ? 'Accepted with EDD' : 'Escalated to MLRO'}`,
      `${hit.entity} — ${hit.tier}. ${disposition === 'escalated' ? 'Referred to MLRO for senior approval.' : 'Enhanced Due Diligence measures applied.'}`,
      disposition === 'escalated' ? 'critical' : 'warning'
    );
  };

  return (
    <div className="space-y-5">
      {/* Sanctions Screening */}
      <div>
        <SectionHeader title="Sanctions Screening Results" subtitle={`${SANCTION_HITS.length} hits require disposition · OFAC SDN · EU Consolidated · UN Security Council · HMT`} />
        <div className="space-y-3 mt-3">
          {SANCTION_HITS.map((hit, idx) => (
            <div key={idx} className={`rounded-xl border p-4 space-y-3 ${sanctionDispositions[idx] === 'confirmed' ? 'border-critical/40 bg-critical/5' : sanctionDispositions[idx] === 'cleared' ? 'border-positive/30 bg-positive/5' : 'border-warning/40 bg-warning/5'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-warning/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield size={15} className="text-warning" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-foreground">{hit.entity}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs font-mono-data bg-surface-4 border border-border px-2 py-0.5 rounded-md text-foreground">{hit.list}</span>
                      <span className="text-xs text-muted-foreground">{hit.matchType}</span>
                      <span className={`text-xs font-mono-data font-bold ${hit.score >= 80 ? 'text-critical' : hit.score >= 60 ? 'text-warning' : 'text-muted-foreground'}`}>Score: {hit.score}/100</span>
                    </div>
                  </div>
                </div>
                {sanctionDispositions[idx] === 'confirmed' && <StatusBadge variant="rejected" label="Confirmed Hit" dot />}
                {sanctionDispositions[idx] === 'cleared' && <StatusBadge variant="active" label="Cleared" dot />}
                {!sanctionDispositions[idx] && <StatusBadge variant="pending" label="Pending Review" dot />}
              </div>

              <div className="p-2.5 rounded-lg bg-surface-4 border border-border">
                <p className="text-xs text-muted-foreground leading-relaxed">{hit.detail}</p>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground font-medium">Officer Disposition Note</p>
                <textarea
                  rows={2}
                  placeholder="Document your analysis and rationale for this sanctions hit…"
                  value={notes[`sanc-${idx}`] || ''}
                  onChange={(e) => setNotes((prev) => ({ ...prev, [`sanc-${idx}`]: e.target.value }))}
                  className="w-full bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-primary"
                />
              </div>

              {!sanctionDispositions[idx] && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSanctionDispose(idx, 'cleared')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-positive/15 text-positive border border-positive/30 hover:bg-positive/25 transition-colors"
                  >
                    <CheckCircle size={12} /> Clear — False Positive
                  </button>
                  <button
                    onClick={() => handleSanctionDispose(idx, 'confirmed')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-critical/15 text-critical border border-critical/30 hover:bg-critical/25 transition-colors"
                  >
                    <XCircle size={12} /> Confirm Hit — Block
                  </button>
                  <button className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-surface-4 text-muted-foreground border border-border hover:text-foreground transition-colors">
                    <ExternalLink size={12} /> View Full Record
                  </button>
                </div>
              )}
              {sanctionDispositions[idx] && (
                <button onClick={() => setSanctionDispositions((prev) => ({ ...prev, [idx]: null }))} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                  <RefreshCw size={10} /> Reset disposition
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* PEP Screening */}
      <div>
        <SectionHeader title="PEP Screening Results" subtitle={`${PEP_HITS.length} PEP hit detected · World-Check · Dow Jones · LexisNexis`} />
        <div className="space-y-3 mt-3">
          {PEP_HITS.map((hit, idx) => (
            <div key={idx} className={`rounded-xl border p-4 space-y-3 ${pepDispositions[idx] === 'escalated' ? 'border-critical/40 bg-critical/5' : pepDispositions[idx] === 'accepted' ? 'border-warning/40 bg-warning/5' : 'border-warning/40 bg-warning/5'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-warning/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Flag size={15} className="text-warning" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-foreground">{hit.entity}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs font-mono-data bg-warning/10 border border-warning/30 px-2 py-0.5 rounded-md text-warning">{hit.tier}</span>
                      <span className="text-xs text-muted-foreground">{hit.relation}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Database: {hit.database} · Matched: {hit.matchDate}</p>
                  </div>
                </div>
                {pepDispositions[idx] === 'accepted' && <StatusBadge variant="pending" label="EDD Applied" dot />}
                {pepDispositions[idx] === 'escalated' && <StatusBadge variant="rejected" label="Escalated" dot />}
                {!pepDispositions[idx] && <StatusBadge variant="pending" label="Pending Review" dot />}
              </div>

              <div className="p-2.5 rounded-lg bg-surface-4 border border-border">
                <p className="text-xs text-muted-foreground">PEP status requires Enhanced Due Diligence (EDD) measures including source of wealth verification, senior management approval, and enhanced ongoing monitoring.</p>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground font-medium">EDD Rationale</p>
                <textarea
                  rows={2}
                  placeholder="Document EDD measures applied and rationale for acceptance or escalation…"
                  value={notes[`pep-${idx}`] || ''}
                  onChange={(e) => setNotes((prev) => ({ ...prev, [`pep-${idx}`]: e.target.value }))}
                  className="w-full bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-primary"
                />
              </div>

              {!pepDispositions[idx] && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePEPDispose(idx, 'accepted')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-warning/15 text-warning border border-warning/30 hover:bg-warning/25 transition-colors"
                  >
                    <CheckCircle size={12} /> Accept with EDD
                  </button>
                  <button
                    onClick={() => handlePEPDispose(idx, 'escalated')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-critical/15 text-critical border border-critical/30 hover:bg-critical/25 transition-colors"
                  >
                    <AlertTriangle size={12} /> Escalate to MLRO
                  </button>
                </div>
              )}
              {pepDispositions[idx] && (
                <button onClick={() => setPepDispositions((prev) => ({ ...prev, [idx]: null }))} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                  <RefreshCw size={10} /> Reset
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Decision Panel ───────────────────────────────────────────────────────────
function DecisionPanel({
  app,
  onDecisionEmit,
  onAddAudit,
}: {
  app: KYCApplication;
  onDecisionEmit: (decision: ReviewDecision, investorStatus: InvestorStatusUpdate, conditions: Condition[], rationale: string) => void;
  onAddAudit: (action: string, detail: string, type: AuditEvent['type']) => void;
}) {
  const [decision, setDecision] = useState<ReviewDecision>(null);
  const [investorStatus, setInvestorStatus] = useState<InvestorStatusUpdate>(null);
  const [rationale, setRationale] = useState('');
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [newCondition, setNewCondition] = useState('');
  const [rejectionCode, setRejectionCode] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [notifyInvestor, setNotifyInvestor] = useState(true);

  const rejectionCodes = [
    'RC-01: Sanctions confirmed hit — onboarding prohibited',
    'RC-02: Document forgery suspected',
    'RC-03: Source of funds unverifiable',
    'RC-04: UBO structure opaque — beneficial ownership unclear',
    'RC-05: High-risk jurisdiction — FATF blacklist',
    'RC-06: Incomplete documentation after 30-day cure period',
    'RC-07: PEP — senior management approval denied',
    'RC-08: AML red flags — STR filed',
  ];

  const addCondition = () => {
    if (!newCondition.trim()) return;
    setConditions((prev) => [...prev, { id: `cond-${Date.now()}`, text: newCondition.trim() }]);
    setNewCondition('');
  };

  const removeCondition = (id: string) => setConditions((prev) => prev.filter((c) => c.id !== id));

  const canSubmit = decision && rationale.trim().length >= 20 && investorStatus &&
    (decision !== 'approve-conditions' || conditions.length > 0) &&
    (decision !== 'reject' || rejectionCode);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onDecisionEmit(decision, investorStatus, conditions, rationale);
    setSubmitted(true);
    onAddAudit(
      decision === 'approve' ? 'Application Approved' : decision === 'approve-conditions' ? 'Approved with Conditions' : 'Application Rejected',
      `Decision: ${decision}. Investor status: ${investorStatus}. ${conditions.length > 0 ? `${conditions.length} condition(s) attached.` : ''} Rationale recorded.`,
      decision === 'reject' ? 'critical' : decision === 'approve-conditions' ? 'warning' : 'success'
    );
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="w-14 h-14 rounded-full bg-positive/15 flex items-center justify-center">
          <CheckCircle size={28} className="text-positive" />
        </div>
        <div className="text-center">
          <p className="font-bold text-lg text-foreground">Decision Dispatched</p>
          <p className="text-sm text-muted-foreground mt-1">
            {decision === 'approve' ? 'Application approved.' : decision === 'approve-conditions' ? 'Approved with conditions attached.' : 'Application rejected.'} Investor status updated to <span className="font-semibold text-foreground">{investorStatus}</span>.
          </p>
          {notifyInvestor && <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1"><Bell size={11} /> Investor notification dispatched.</p>}
        </div>
        <button onClick={() => setSubmitted(false)} className="btn-secondary text-xs gap-1.5"><RefreshCw size={12} /> Revise Decision</button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SectionHeader title="Compliance Decision" subtitle="Review all sections before issuing a final decision. Decision is logged to immutable audit trail." />

      {/* Decision selection */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Decision</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { value: 'approve' as ReviewDecision, label: 'Approve', icon: ThumbsUp, desc: 'Application meets all requirements. Investor cleared for onboarding.', color: 'positive' },
            { value: 'approve-conditions' as ReviewDecision, label: 'Approve with Conditions', icon: AlertCircle, desc: 'Approved subject to conditions. Investor access restricted until conditions met.', color: 'warning' },
            { value: 'reject' as ReviewDecision, label: 'Reject', icon: ThumbsDown, desc: 'Application does not meet requirements. Investor blocked from onboarding.', color: 'negative' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDecision(opt.value)}
              className={`p-3 rounded-xl border text-left transition-all ${decision === opt.value
                ? opt.color === 'positive' ? 'border-positive/50 bg-positive/10' : opt.color === 'warning' ? 'border-warning/50 bg-warning/10' : 'border-negative/50 bg-negative/10' :'border-border bg-surface-3 hover:border-border-hover hover:bg-surface-4'}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <opt.icon size={14} className={decision === opt.value ? opt.color === 'positive' ? 'text-positive' : opt.color === 'warning' ? 'text-warning' : 'text-negative' : 'text-muted-foreground'} />
                <p className={`text-sm font-semibold ${decision === opt.value ? opt.color === 'positive' ? 'text-positive' : opt.color === 'warning' ? 'text-warning' : 'text-negative' : 'text-foreground'}`}>{opt.label}</p>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Investor status dispatch */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Investor Status Update</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { value: 'eligible' as InvestorStatusUpdate, label: 'Eligible', desc: 'Full portfolio access', color: 'positive' },
            { value: 'restricted' as InvestorStatusUpdate, label: 'Restricted', desc: 'Limited access — conditions apply', color: 'warning' },
            { value: 'pending-edd' as InvestorStatusUpdate, label: 'Pending EDD', desc: 'Enhanced due diligence required', color: 'info' },
            { value: 'blocked' as InvestorStatusUpdate, label: 'Blocked', desc: 'No portfolio access', color: 'negative' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setInvestorStatus(opt.value)}
              className={`p-2.5 rounded-lg border text-left transition-all ${investorStatus === opt.value
                ? opt.color === 'positive' ? 'border-positive/50 bg-positive/10' : opt.color === 'warning' ? 'border-warning/50 bg-warning/10' : opt.color === 'negative' ? 'border-negative/50 bg-negative/10' : 'border-primary/50 bg-primary/10' :'border-border bg-surface-3 hover:border-border-hover hover:bg-surface-4'}`}
            >
              <p className={`text-xs font-semibold mb-0.5 ${investorStatus === opt.value ? opt.color === 'positive' ? 'text-positive' : opt.color === 'warning' ? 'text-warning' : opt.color === 'negative' ? 'text-negative' : 'text-primary' : 'text-foreground'}`}>{opt.label}</p>
              <p className="text-xs text-muted-foreground">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Conditions (if approve-conditions) */}
      {decision === 'approve-conditions' && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Approval Conditions</p>
          <div className="space-y-2">
            {conditions.map((c) => (
              <div key={c.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-warning/5 border border-warning/30">
                <AlertCircle size={13} className="text-warning mt-0.5 flex-shrink-0" />
                <p className="flex-1 text-xs text-foreground">{c.text}</p>
                <button onClick={() => removeCondition(c.id)} className="text-muted-foreground hover:text-negative transition-colors flex-shrink-0">
                  <Minus size={13} />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add a condition (e.g. Provide updated source of funds within 30 days)…"
                value={newCondition}
                onChange={(e) => setNewCondition(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCondition()}
                className="flex-1 bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary"
              />
              <button onClick={addCondition} className="flex items-center gap-1 px-3 py-2 rounded-md text-xs font-medium bg-warning/15 text-warning border border-warning/30 hover:bg-warning/25 transition-colors flex-shrink-0">
                <Plus size={12} /> Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection code */}
      {decision === 'reject' && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rejection Code</p>
          <select
            value={rejectionCode}
            onChange={(e) => setRejectionCode(e.target.value)}
            className="w-full bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
          >
            <option value="">Select rejection code…</option>
            {rejectionCodes.map((code) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </div>
      )}

      {/* Rationale */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Decision Rationale <span className="text-muted-foreground font-normal normal-case">(min. 20 characters)</span></p>
        <textarea
          rows={4}
          placeholder="Document your compliance analysis, risk assessment findings, and rationale for this decision. This will be appended to the immutable audit trail…"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          className="w-full bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-primary"
        />
        <p className="text-xs text-muted-foreground text-right">{rationale.length} chars</p>
      </div>

      {/* Notification toggle */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-3 border border-border">
        <Bell size={14} className="text-muted-foreground flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs font-medium text-foreground">Dispatch investor status notification</p>
          <p className="text-xs text-muted-foreground">Automated email/portal notification sent to investor upon decision dispatch.</p>
        </div>
        <button
          onClick={() => setNotifyInvestor(!notifyInvestor)}
          className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${notifyInvestor ? 'bg-primary' : 'bg-surface-4 border border-border'}`}
        >
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${notifyInvestor ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {/* Submit */}
      <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock size={12} />
          Decision is final and appended to immutable audit trail.
        </div>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${canSubmit
            ? decision === 'reject' ? 'bg-negative text-white hover:bg-negative/90' : decision === 'approve-conditions' ? 'bg-warning text-white hover:bg-warning/90' : 'btn-primary' :'bg-surface-4 text-muted-foreground border border-border cursor-not-allowed'}`}
        >
          <Send size={14} />
          {decision === 'approve' ? 'Approve & Dispatch' : decision === 'approve-conditions' ? 'Approve with Conditions' : decision === 'reject' ? 'Reject & Notify' : 'Dispatch Decision'}
        </button>
      </div>
    </div>
  );
}

// ─── Audit Trail ──────────────────────────────────────────────────────────────
function AuditTrail({ events }: { events: AuditEvent[] }) {
  const typeIcon = (type: AuditEvent['type']) => {
    if (type === 'critical') return <AlertTriangle size={12} className="text-critical" />;
    if (type === 'warning') return <AlertCircle size={12} className="text-warning" />;
    if (type === 'success') return <CheckCircle size={12} className="text-positive" />;
    return <Activity size={12} className="text-primary" />;
  };

  return (
    <div className="space-y-2">
      {[...events].reverse().map((event, idx) => (
        <div key={idx} className={`flex gap-3 p-3 rounded-lg border ${event.type === 'critical' ? 'border-critical/20 bg-critical/5' : event.type === 'warning' ? 'border-warning/20 bg-warning/5' : event.type === 'success' ? 'border-positive/20 bg-positive/5' : 'border-border bg-surface-3'}`}>
          <div className="flex-shrink-0 mt-0.5">{typeIcon(event.type)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <p className="text-xs font-semibold text-foreground">{event.action}</p>
              <p className="text-xs font-mono-data text-muted-foreground flex-shrink-0">{event.ts}</p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{event.detail}</p>
            <p className="text-xs text-muted-foreground mt-0.5">by {event.actor}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Application Detail View ──────────────────────────────────────────────────
function ApplicationDetail({ app, onBack }: { app: KYCApplication; onBack: () => void }) {
  const [activeTab, setActiveTab] = useState<'overview' | 'sanctions-pep' | 'decision' | 'audit'>('overview');
  const [auditLog, setAuditLog] = useState<AuditEvent[]>(INITIAL_AUDIT);
  const [decisionEmitted, setDecisionEmitted] = useState(false);

  const addAuditEvent = (action: string, detail: string, type: AuditEvent['type'] = 'info') => {
    setAuditLog((prev) => [...prev, { ts: '2026-08-20 17:04', actor: 'L. Okafor', action, detail, type }]);
  };

  const handleDecisionEmit = (decision: ReviewDecision, investorStatus: InvestorStatusUpdate, conditions: Condition[], rationale: string) => {
    setDecisionEmitted(true);
  };

  const tabs = [
    { id: 'overview', label: 'Application Overview', icon: FileText },
    { id: 'sanctions-pep', label: 'Sanctions & PEP', icon: Shield, badge: app.sanctionStatus !== 'clear' ? '!' : undefined },
    { id: 'decision', label: 'Decision & Dispatch', icon: Send },
    { id: 'audit', label: 'Audit Trail', icon: Activity, badge: auditLog.length },
  ] as const;

  return (
    <div className="space-y-5">
      {/* Breadcrumb + header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <button onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
              <ChevronLeft size={12} /> Queue
            </button>
            <ChevronRight size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-mono-data">{app.refNo}</span>
          </div>
          <h2 className="text-xl font-bold text-foreground tracking-tight">{app.name}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{app.type} · {app.jurisdiction} · {app.queue}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono-data text-muted-foreground bg-surface-3 border border-border px-2 py-1 rounded-md">
            {app.assignee}
          </span>
          {appStatusBadge(app.status)}
          <button className="btn-secondary text-xs gap-1.5"><Download size={12} /> Export</button>
        </div>
      </div>

      {/* Critical banner */}
      {(app.sanctionStatus === 'partial-match' || app.sanctionStatus === 'confirmed-hit') && (
        <div className="flex items-start gap-3 p-3 rounded-xl border border-critical/40 bg-critical/10">
          <AlertTriangle size={15} className="text-critical flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-critical">Sanctions Hit Requires Disposition Before Decision</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {app.sanctionStatus === 'partial-match' ? `Partial match detected (score: ${app.sanctionScore}/100). Review Sanctions & PEP tab before issuing a compliance decision.` : 'Confirmed sanctions hit. Onboarding is blocked. Escalate to MLRO immediately.'}
            </p>
          </div>
          <button onClick={() => setActiveTab('sanctions-pep')} className="flex-shrink-0 btn-ghost text-xs text-critical gap-1">
            Review <ArrowRight size={11} />
          </button>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Documents" value={`${app.docsVerified}/${app.docsTotal}`} subValue="verified" variant={app.docsVerified === app.docsTotal ? 'positive' : 'warning'} icon={FileText} iconColor={app.docsVerified === app.docsTotal ? 'text-positive' : 'text-warning'} mono />
        <MetricCard label="Risk Level" value={app.riskLevel.charAt(0).toUpperCase() + app.riskLevel.slice(1)} subValue={app.queue} variant={app.riskLevel === 'critical' ? 'critical' : app.riskLevel === 'high' ? 'negative' : app.riskLevel === 'medium' ? 'warning' : 'positive'} icon={BarChart3} iconColor="text-primary" />
        <MetricCard label="AML Flags" value={app.amlFlags.toString()} subValue={app.amlFlags > 0 ? 'Requires review' : 'No flags'} variant={app.amlFlags > 1 ? 'critical' : app.amlFlags === 1 ? 'warning' : 'positive'} icon={AlertTriangle} iconColor={app.amlFlags > 0 ? 'text-warning' : 'text-positive'} />
        <MetricCard label="Sanctions" value={app.sanctionStatus === 'clear' ? 'Clear' : app.sanctionStatus === 'partial-match' ? 'Partial Match' : 'Confirmed Hit'} subValue={app.sanctionScore ? `Score: ${app.sanctionScore}/100` : 'No hits'} variant={app.sanctionStatus === 'confirmed-hit' ? 'critical' : app.sanctionStatus === 'partial-match' ? 'warning' : 'positive'} icon={Shield} iconColor={app.sanctionStatus !== 'clear' ? 'text-warning' : 'text-positive'} />
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`portal-tab flex items-center gap-1.5 ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon size={13} />
            {tab.label}
            {tab.badge !== undefined && (
              <span className={`text-xs font-mono-data px-1.5 py-0.5 rounded-full ${tab.badge === '!' ? 'bg-critical/20 text-critical' : 'bg-surface-4 text-muted-foreground border border-border'}`}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="glass-card rounded-xl p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-xs">
              <div><p className="text-muted-foreground mb-0.5">Reference No.</p><p className="font-mono-data text-foreground font-medium">{app.refNo}</p></div>
              <div><p className="text-muted-foreground mb-0.5">Applicant Type</p><p className="text-foreground font-medium">{app.type}</p></div>
              <div><p className="text-muted-foreground mb-0.5">Jurisdiction</p><p className="text-foreground font-medium">{app.jurisdiction}</p></div>
              <div><p className="text-muted-foreground mb-0.5">Submitted</p><p className="font-mono-data text-foreground">{app.submittedAt}</p></div>
              {app.incorporationNo && <div><p className="text-muted-foreground mb-0.5">Incorporation No.</p><p className="font-mono-data text-foreground">{app.incorporationNo}</p></div>}
              {app.fatcaStatus && <div><p className="text-muted-foreground mb-0.5">FATCA Status</p><p className="text-foreground font-medium">{app.fatcaStatus}</p></div>}
              <div><p className="text-muted-foreground mb-0.5">PEP Declared</p><p className={`font-medium ${app.pepDeclared ? 'text-warning' : 'text-positive'}`}>{app.pepDeclared ? 'Yes' : 'No'}</p></div>
              <div><p className="text-muted-foreground mb-0.5">Email</p><p className="text-foreground truncate">{app.email}</p></div>
              {app.uboCount && <div><p className="text-muted-foreground mb-0.5">UBO Count</p><p className="font-mono-data text-foreground font-medium">{app.uboCount}</p></div>}
              <div><p className="text-muted-foreground mb-0.5">Queue</p><p className="text-foreground font-medium">{app.queue}</p></div>
              <div><p className="text-muted-foreground mb-0.5">Assignee</p><p className="text-foreground font-medium">{app.assignee}</p></div>
              <div><p className="text-muted-foreground mb-0.5">Last Activity</p><p className="font-mono-data text-foreground">{app.lastActivity}</p></div>
            </div>
          </div>

          {/* Compliance checklist */}
          <div className="glass-card rounded-xl p-4 space-y-3">
            <p className="text-sm font-semibold text-foreground">Compliance Checklist</p>
            {[
              { label: 'Identity documents submitted', done: app.docsVerified >= 2 },
              { label: 'Liveness / biometric verification', done: app.type === 'Individual (KYC)' ? true : false },
              { label: 'Sanctions screening completed', done: app.sanctionStatus !== 'clear' ? false : true },
              { label: 'PEP screening completed', done: app.pepTier === 'none' },
              { label: 'AML risk assessment', done: app.amlFlags === 0 },
              { label: 'UBO declarations (if applicable)', done: app.uboCount ? app.docsVerified >= app.docsTotal - 1 : true },
              { label: 'Source of funds verified', done: app.riskLevel === 'low' },
              { label: 'FATCA/CRS classification', done: !!app.fatcaStatus },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-positive/15' : 'bg-surface-4 border border-border'}`}>
                  {item.done ? <CheckCircle size={12} className="text-positive" /> : <Clock size={12} className="text-muted-foreground" />}
                </div>
                <p className={`text-xs ${item.done ? 'text-foreground' : 'text-muted-foreground'}`}>{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SANCTIONS & PEP ── */}
      {activeTab === 'sanctions-pep' && (
        <SanctionPEPPanel app={app} onAddAudit={addAuditEvent} />
      )}

      {/* ── DECISION ── */}
      {activeTab === 'decision' && (
        <DecisionPanel app={app} onDecisionEmit={handleDecisionEmit} onAddAudit={addAuditEvent} />
      )}

      {/* ── AUDIT TRAIL ── */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          <SectionHeader title="Immutable Audit Trail" subtitle={`${auditLog.length} events recorded · Tamper-evident log · All actions timestamped`} />
          <AuditTrail events={auditLog} />
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function KYCReviewWorkflow() {
  const [selectedApp, setSelectedApp] = useState<KYCApplication | null>(null);
  const [filterRisk, setFilterRisk] = useState<RiskLevel | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<AppStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredApps = APPLICATIONS.filter((app) => {
    const matchRisk = filterRisk === 'all' || app.riskLevel === filterRisk;
    const matchStatus = filterStatus === 'all' || app.status === filterStatus;
    const matchSearch = !searchQuery || app.name.toLowerCase().includes(searchQuery.toLowerCase()) || app.refNo.toLowerCase().includes(searchQuery.toLowerCase());
    return matchRisk && matchStatus && matchSearch;
  });

  const queueMetrics = {
    total: APPLICATIONS.length,
    critical: APPLICATIONS.filter((a) => a.riskLevel === 'critical').length,
    sanctionHits: APPLICATIONS.filter((a) => a.sanctionStatus !== 'clear').length,
    pepFlags: APPLICATIONS.filter((a) => a.pepTier !== 'none').length,
    amlFlags: APPLICATIONS.reduce((sum, a) => sum + a.amlFlags, 0),
  };

  if (selectedApp) {
    return (
      <div className="space-y-0">
        <ApplicationDetail app={selectedApp} onBack={() => setSelectedApp(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">KYC/KYB Compliance Review</h1>
          <p className="text-sm text-muted-foreground mt-1">Review submitted applications · Approve / Reject · Flag PEP & Sanctions · Dispatch investor status</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs gap-1.5"><RefreshCw size={12} /> Refresh Queue</button>
          <button className="btn-primary text-xs gap-1.5"><Flag size={12} /> File STR</button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MetricCard label="Total Queue" value={queueMetrics.total.toString()} subValue="applications" variant="default" icon={Inbox} iconColor="text-primary" mono />
        <MetricCard label="Critical Risk" value={queueMetrics.critical.toString()} subValue="require EDD" variant="critical" icon={AlertTriangle} iconColor="text-critical" mono />
        <MetricCard label="Sanction Hits" value={queueMetrics.sanctionHits.toString()} subValue="pending disposition" variant={queueMetrics.sanctionHits > 0 ? 'warning' : 'positive'} icon={Shield} iconColor={queueMetrics.sanctionHits > 0 ? 'text-warning' : 'text-positive'} mono />
        <MetricCard label="PEP Flags" value={queueMetrics.pepFlags.toString()} subValue="EDD required" variant={queueMetrics.pepFlags > 0 ? 'warning' : 'positive'} icon={Flag} iconColor={queueMetrics.pepFlags > 0 ? 'text-warning' : 'text-positive'} mono />
        <MetricCard label="AML Flags" value={queueMetrics.amlFlags.toString()} subValue="across all cases" variant={queueMetrics.amlFlags > 3 ? 'critical' : queueMetrics.amlFlags > 0 ? 'warning' : 'positive'} icon={Zap} iconColor={queueMetrics.amlFlags > 0 ? 'text-warning' : 'text-positive'} mono />
      </div>

      {/* Critical banner */}
      {queueMetrics.sanctionHits > 0 && (
        <div className="flex items-start gap-3 p-3 rounded-xl border border-critical/40 bg-critical/10">
          <AlertTriangle size={15} className="text-critical flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-critical">{queueMetrics.sanctionHits} Application{queueMetrics.sanctionHits > 1 ? 's' : ''} with Unresolved Sanctions Hits</p>
            <p className="text-xs text-muted-foreground mt-0.5">Sanctions hits must be disposed (cleared or confirmed) before a compliance decision can be issued. Select the application to review.</p>
          </div>
        </div>
      )}

      {/* Filters + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name or reference…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-surface-3 border border-border rounded-lg pl-8 pr-3 py-2 text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-muted-foreground" />
          <select
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value as RiskLevel | 'all')}
            className="bg-surface-3 border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
          >
            <option value="all">All Risk Levels</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as AppStatus | 'all')}
            className="bg-surface-3 border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:border-primary"
          >
            <option value="all">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="under-review">Under Review</option>
            <option value="pending-info">Pending Info</option>
            <option value="approved">Approved</option>
            <option value="approved-conditions">Approved w/ Conditions</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Application queue */}
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-muted-foreground">{filteredApps.length} application{filteredApps.length !== 1 ? 's' : ''} shown</p>
        </div>
        {filteredApps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Inbox size={32} className="text-muted-foreground mb-3" />
            <p className="text-sm font-medium text-foreground">No applications match your filters</p>
            <p className="text-xs text-muted-foreground mt-1">Try adjusting the risk level or status filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filteredApps.map((app) => (
              <ApplicationQueueRow
                key={app.id}
                app={app}
                selected={selectedApp?.id === app.id}
                onSelect={() => setSelectedApp(app)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
